import prettyBytes from 'pretty-bytes'

import {
  CrashDateTimePrecision,
  CrashSeverityLevel,
  CrashTypeCategory,
  IncidentReportType,
  OpenDataSource
} from '@roadfootage/api-client'
import { regionConfig } from '@/config/region'

const { locale, currency } = regionConfig

const numberFmt = new Intl.NumberFormat(locale)
const currencyFmt = new Intl.NumberFormat(locale, { style: 'currency', currency })
const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'short' })
const dateTimeFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' })
const timeFmt = new Intl.DateTimeFormat(locale, { timeStyle: 'short' })

// Intl throws a RangeError on an Invalid Date, so formatters treat one like a missing value.
function toValidDate(d: Date | string | null | undefined): Date | null {
  if (!d) return null
  const date = new Date(d)
  return isNaN(date.getTime()) ? null : date
}

export function formatNumber(n: number): string {
  return numberFmt.format(n)
}

export function formatCurrency(n: number): string {
  return currencyFmt.format(n)
}

export function formatDate(d: Date | string | null | undefined): string {
  const date = toValidDate(d)
  return date ? dateFmt.format(date) : ''
}

export function formatDateTime(d: Date | string | null | undefined): string {
  const date = toValidDate(d)
  return date ? dateTimeFmt.format(date) : ''
}

/**
 * Compact age for lists: "now", "2m", "1h", "3d", then the date once it is a week old. Pass `now` in tests.
 */
export function formatRelativeTime(
  d: Date | string | null | undefined,
  now: Date = new Date()
): string {
  const date = toValidDate(d)
  if (!date) return ''
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  if (seconds < 60) return 'now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`
  return dateFmt.format(date)
}

export function formatTime(d: Date | string | null | undefined): string {
  const date = toValidDate(d)
  return date ? timeFmt.format(date) : ''
}

export function formatDateTimeIntl(d: Date | null | undefined): string {
  return d ? dateTimeFmt.format(d) : ''
}

export function formatIncidentDateTime(
  dateTime: Date | string | null | undefined,
  timezone: string | null | undefined
): string {
  if (!dateTime) return 'Date unknown'
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone ?? undefined
  }).format(new Date(dateTime))
}

export function formatFileSize(bytes: number): string {
  return prettyBytes(bytes)
}

const INCIDENT_TYPE_LABELS: Record<IncidentReportType, string> = {
  [IncidentReportType.TrafficAccident]: 'Traffic Accident',
  [IncidentReportType.BadDriving]: 'Bad Driving',
  [IncidentReportType.RoadRage]: 'Road Rage',
  [IncidentReportType.LawEnforcement]: 'Instant Karma'
}
export function formatIncidentType(type: string | null | undefined): string {
  return INCIDENT_TYPE_LABELS[type as IncidentReportType] ?? 'Unclassified Incident'
}

const CRASH_SEVERITY_LABELS: Record<CrashSeverityLevel, string> = {
  [CrashSeverityLevel.PropertyDamageOnly]: 'Property damage only',
  [CrashSeverityLevel.MinorInjury]: 'Minor injury',
  [CrashSeverityLevel.Injury]: 'Injury',
  [CrashSeverityLevel.SeriousInjury]: 'Serious injury',
  [CrashSeverityLevel.Fatal]: 'Fatal'
}
export function formatCrashSeverity(severity: string | null | undefined): string {
  return CRASH_SEVERITY_LABELS[severity as CrashSeverityLevel] ?? 'Severity unknown'
}

const CRASH_TYPE_LABELS: Record<CrashTypeCategory, string> = {
  [CrashTypeCategory.Unknown]: 'Type unknown',
  [CrashTypeCategory.RearEnd]: 'Rear end',
  [CrashTypeCategory.HeadOn]: 'Head on',
  [CrashTypeCategory.Sideswipe]: 'Sideswipe',
  [CrashTypeCategory.RightAngle]: 'Right angle',
  [CrashTypeCategory.TurningMovement]: 'Turning movement',
  [CrashTypeCategory.HitPedestrian]: 'Hit pedestrian',
  [CrashTypeCategory.HitCyclist]: 'Hit cyclist',
  [CrashTypeCategory.HitObject]: 'Hit object',
  [CrashTypeCategory.Rollover]: 'Rollover',
  [CrashTypeCategory.HitAnimal]: 'Hit animal',
  [CrashTypeCategory.Other]: 'Other'
}
export function formatCrashType(type: string | null | undefined): string {
  return CRASH_TYPE_LABELS[type as CrashTypeCategory] ?? 'Type unknown'
}

/** "QLD open data" for AUS_QLD: the state or territory that published the crash record. */
export function formatCrashSource(source: string | null | undefined): string {
  const known = Object.values(OpenDataSource).includes(source as OpenDataSource)
  return known ? `${(source as string).replace('AUS_', '')} open data` : 'Open data'
}

/**
 * A recorded crash's time, read in the crash's own timezone and only as precisely as the source knows it: a Month crash has
 * no real day or time, an Hour crash has no real day.
 */
export function formatCrashDateTime(
  dateTime: Date | string | null | undefined,
  timezone: string | null | undefined,
  precision: CrashDateTimePrecision | null | undefined
): string {
  const date = toValidDate(dateTime)
  if (!date) return 'Date unknown'
  const timeZone = timezone || undefined
  if (precision === CrashDateTimePrecision.Month) {
    const month = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      timeZone
    }).format(date)
    return `${month} (month only)`
  }
  if (precision === CrashDateTimePrecision.Hour) {
    const hour = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      hour: 'numeric',
      timeZone
    }).format(date)
    return `${hour} (day unknown)`
  }
  return formatIncidentDateTime(date, timezone)
}
